import { agentHubPaths } from '../../util/constants';
import { PROVISIONING_PATH_BY_PLATFORM, type ProvisioningPlatform } from './provisioning/paths';

export type AgentHubPageDescriptionPart = {
  text: string;
  emphasized?: boolean;
};

export type AgentHubNavItem = {
  label: string;
  path: string;
  description?: readonly AgentHubPageDescriptionPart[];
};

/** Sidebar nav + page headers for Agent Hub. Order matches the original horizontal tabs. */
export const AGENT_HUB_NAV_ITEMS: readonly AgentHubNavItem[] = [
  { label: 'Dashboard', path: agentHubPaths.DASHBOARD },
  {
    label: 'Agents',
    path: agentHubPaths.AGENTS,
    description: [
      { text: 'Each agent has a unique identity: ', emphasized: true },
      { text: 'Name, avatar, tone, and voice differ per agent.' },
    ],
  },
  { label: 'Rules', path: agentHubPaths.PROMPT_MANAGEMENT },
  {
    label: 'Engagement',
    path: agentHubPaths.ENGAGEMENT,
    description: [
      { text: 'Configure agent prompting and behavior. ' },
      { text: 'Prompts are shared across all agents.', emphasized: true },
    ],
  },
  { label: 'Escalation', path: agentHubPaths.ESCALATION },
  { label: 'Support', path: agentHubPaths.SUPPORT },
  {
    label: 'Checkout Mode',
    path: agentHubPaths.CHECKOUT_MODE,
    description: [
      { text: 'Configure how agents handle checkout with shoppers. ' },
      { text: 'Checkout configurations are shared across all agents.', emphasized: true },
    ],
  },
  {
    label: 'Workflows',
    path: agentHubPaths.WORKFLOWS,
    description: [
      { text: 'Create rules for your agent to be followed during interactions with shoppers. ' },
      { text: 'Rules are shared across all agents.', emphasized: true },
    ],
  },
  { label: 'Catalog', path: agentHubPaths.CATALOG },
];

export const PROVISIONING_NAV_ITEMS: Record<ProvisioningPlatform, AgentHubNavItem> = {
  shopify: {
    label: 'Provisioning',
    path: PROVISIONING_PATH_BY_PLATFORM.shopify,
    description: [
      { text: 'Complete these setup steps in ' },
      { text: 'Shopify store dashboard.', emphasized: true },
    ],
  },
  bigcommerce: {
    label: 'Provisioning',
    path: PROVISIONING_PATH_BY_PLATFORM.bigcommerce,
    description: [
      { text: 'Complete these setup steps in ' },
      { text: 'BigCommerce store dashboard.', emphasized: true },
    ],
  },
  squarespace: {
    label: 'Provisioning',
    path: PROVISIONING_PATH_BY_PLATFORM.squarespace,
    description: [
      { text: 'Complete these setup steps in your ' },
      { text: 'Squarespace site admin.', emphasized: true },
    ],
  },
};

/** Sidebar nav items, plus Provisioning for platforms that have a merchant-facing checklist. */
export const getAgentHubNavItems = (
  platform: ProvisioningPlatform | null = null,
): readonly AgentHubNavItem[] =>
  platform ? [...AGENT_HUB_NAV_ITEMS, PROVISIONING_NAV_ITEMS[platform]] : AGENT_HUB_NAV_ITEMS;

const normalizePath = (pathname: string): string => pathname.toLowerCase();

export const getAgentHubPageHeaderForPath = (
  pathname: string,
  platform: ProvisioningPlatform | null = null,
): AgentHubNavItem | undefined =>
  getAgentHubNavItems(platform).find(
    (item) => normalizePath(item.path) === normalizePath(pathname),
  );
